import { useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { AddButton, Cell, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, tempPassword, useLoad, when } from './kit';

interface Org {
  id: string;
  slug: string;
  name: string;
  status: string;
  plan: string | null;
  quotas: { agents?: number; licenses?: number | null; concurrent_calls?: number };
  created_at: string;
  users: number;
  agents: number;
  licensesInUse: number;
}

/**
 * The provider's console (platform admins only): every organization on this
 * server, with licenses and agents against what it has bought, and the
 * limits to change. A new organization comes with its first admin.
 */
export function Provider() {
  const [orgs, reload] = useLoad<Org[]>('/platform/orgs');
  const [editing, setEditing] = useState<Org | 'new' | null>(null);
  const done = () => {
    setEditing(null);
    reload();
  };

  return (
    <>
      <SectionHeader
        title="Organizations"
        lead="Every company on this server. Licenses limit how many of their people can be signed in at once; the agent limit caps how many agents they can have. Their admins see their own use under Admin > Licenses."
        action={<AddButton onClick={() => setEditing('new')}>New organization</AddButton>}
      />
      <Table head={['Organization', 'Licenses in use', 'Agents', 'Users', 'Created']} empty={orgs?.length === 0 && 'No organizations yet.'}>
        {(orgs ?? []).map((o) => {
          const cap = o.quotas.licenses ?? null;
          const full = cap !== null && o.licensesInUse >= cap;
          return (
            <Row key={o.id} onClick={() => setEditing(o)}>
              <Cell>
                <span className="block font-medium">{o.name}</span>
                <span className="text-xs text-ink-3">
                  {o.slug}
                  {o.status !== 'active' ? ` · ${o.status}` : ''}
                </span>
              </Cell>
              <Cell className="tabular">
                {o.licensesInUse}
                {cap !== null ? <span className="text-ink-3"> of {cap}</span> : <span className="text-ink-3"> (no limit)</span>}
                {full ? <span className="ml-2"><Pill tone="warn">Full</Pill></span> : null}
              </Cell>
              <Cell className="tabular">
                {o.agents}
                <span className="text-ink-3"> of {o.quotas.agents ?? 50}</span>
              </Cell>
              <Cell className="tabular">{o.users}</Cell>
              <Cell className="whitespace-nowrap text-ink-2">{when(o.created_at)}</Cell>
            </Row>
          );
        })}
      </Table>
      {editing === 'new' ? <NewOrg onDone={done} /> : editing ? <Limits org={editing} onDone={done} /> : null}
    </>
  );
}

/** A count field: blank means "no limit" where that is allowed. */
function Count({ label, hint, value, onChange }: { label: string; hint: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label} hint={hint}>
      {(id) => <input id={id} inputMode="numeric" className={`${inputClass} tabular`} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 5))} />}
    </Field>
  );
}

function Limits({ org, onDone }: { org: Org; onDone: () => void }) {
  const [licenses, setLicenses] = useState(org.quotas.licenses ? String(org.quotas.licenses) : '');
  const [agents, setAgents] = useState(String(org.quotas.agents ?? 50));
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    const ok = await act(
      () => api('PATCH', `/platform/orgs/${org.id}/quotas`, { licenses: licenses ? Number(licenses) : null, agents: Number(agents) || 50 }),
      `${org.name}: limits saved`,
    );
    setBusy(false);
    if (ok) onDone();
  }

  const below = licenses !== '' && Number(licenses) < org.licensesInUse;
  return (
    <Drawer
      title={org.name}
      open
      onClose={onDone}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || Number(agents) < 1 || licenses === '0'} onClick={() => void save()}>
            Save limits
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
        </>
      }
    >
      <p className="mb-5 text-sm text-ink-2">
        {org.licensesInUse} signed in now · {org.agents} agents · {org.users} users. Changes apply within seconds and are in the audit log.
      </p>
      <Count label="Licenses" hint="People signed in at once (admins excepted). Leave blank for no limit." value={licenses} onChange={setLicenses} />
      {below ? (
        <p className="-mt-2 mb-4 text-xs text-wrapup">
          {org.licensesInUse} people are signed in now. Nobody is signed out; new sign-ins wait until fewer than {licenses} are in.
        </p>
      ) : null}
      <Count label="Agent limit" hint="Active users with the agent role. Admins, supervisors, QA and managers do not count." value={agents} onChange={setAgents} />
    </Drawer>
  );
}

function NewOrg({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ name: '', slug: '', adminName: '', adminEmail: '', licenses: '', agents: '50' });
  const [password] = useState(tempPassword);
  const [created, setCreated] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));

  async function create() {
    setBusy(true);
    const ok = await act(
      () =>
        api('POST', '/platform/orgs', {
          name: f.name.trim(),
          slug: f.slug,
          admin: { name: f.adminName.trim(), email: f.adminEmail.trim(), password },
          licenses: f.licenses ? Number(f.licenses) : null,
          agents: Number(f.agents) || 50,
        }).then(() => true),
      `${f.name.trim()} created`,
    );
    setBusy(false);
    if (ok) setCreated(true);
  }

  if (created) {
    return (
      <Drawer title="Share the sign-in details" open onClose={onDone} footer={<Button tone="primary" className="px-4 py-2" onClick={onDone}>Done</Button>}>
        <p className="mb-4 text-sm text-ink-2">Send these to {f.adminName.trim()}. The password is shown once; they can change it from their status menu after signing in.</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-xl border border-line p-4 text-sm">
          <dt className="text-ink-3">Email</dt>
          <dd>{f.adminEmail.trim()}</dd>
          <dt className="text-ink-3">Password</dt>
          <dd className="tabular select-all">{password}</dd>
        </dl>
      </Drawer>
    );
  }

  const slugOk = /^[a-z][a-z0-9]{1,19}$/.test(f.slug);
  return (
    <Drawer
      title="New organization"
      open
      onClose={onDone}
      footer={
        <>
          <Button
            tone="primary"
            className="px-4 py-2"
            disabled={busy || f.name.trim().length < 2 || !slugOk || f.adminName.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(f.adminEmail)}
            onClick={() => void create()}
          >
            Create
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
        </>
      }
    >
      <Field label="Company name">{(id) => <input id={id} className={inputClass} value={f.name} onChange={(e) => {
        const name = e.target.value;
        // Suggest a short name from the company name until one is typed
        setF((x) => ({ ...x, name, slug: x.slug && x.slug !== suggest(x.name) ? x.slug : suggest(name) }));
      }} />}</Field>
      <Field label="Short name" hint="2–20 lowercase letters or digits, starting with a letter. Used inside Nova; it cannot change later.">
        {(id) => <input id={id} className={`${inputClass} tabular`} value={f.slug} onChange={(e) => set('slug')(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20))} />}
      </Field>
      <Field label="Their admin's name">{(id) => <input id={id} className={inputClass} value={f.adminName} onChange={(e) => set('adminName')(e.target.value)} />}</Field>
      <Field label="Their admin's email" hint="Their admin sets up users, queues and numbers.">
        {(id) => <input id={id} type="email" className={inputClass} value={f.adminEmail} onChange={(e) => set('adminEmail')(e.target.value)} />}
      </Field>
      <Count label="Licenses" hint="People signed in at once (admins excepted). Leave blank for no limit." value={f.licenses} onChange={set('licenses')} />
      <Count label="Agent limit" hint="Active users with the agent role." value={f.agents} onChange={set('agents')} />
    </Drawer>
  );
}

const suggest = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/^[0-9]+/, '').slice(0, 20);
